import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useRef,
} from "react"

import {
  showErrorToast,
  showInfoToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"
import * as Sentry from "@sentry/react"
import { RecaptchaVerifier } from "firebase/auth"

import { auth } from "@firebaseConfig/clientApp"

interface IRecaptchaContextProvider {
  verifier: React.MutableRefObject<RecaptchaVerifier | null>
  recaptchaVerifiedState: React.MutableRefObject<boolean | null>
  recaptchaErrorhandler: (err: any) => void
  clearRecaptchaToken: () => void
  isProcessing: React.MutableRefObject<boolean>
  removeRecaptchaElement: () => void
  embedTheRecaptchaElement: (
    recaptchaRef: React.MutableRefObject<HTMLDivElement | null>,
  ) => void
  initializeTheRecaptchaVerifier: (
    recaptchaRef: React.MutableRefObject<HTMLDivElement | null>,
  ) => void
}

const RecaptchaContext = createContext<IRecaptchaContextProvider>({
  verifier: { current: null },
  recaptchaVerifiedState: { current: null },
  recaptchaErrorhandler: (err: any) => {},
  clearRecaptchaToken: () => {},
  isProcessing: { current: false },
  removeRecaptchaElement: () => {},
  embedTheRecaptchaElement: (
    recaptchaRef: React.MutableRefObject<HTMLDivElement | null>,
  ) => {},
  initializeTheRecaptchaVerifier: (
    recaptchaRef: React.MutableRefObject<HTMLDivElement | null>,
  ) => {},
})

export const useRecaptcha = () => {
  const context = useContext(RecaptchaContext)
  if (!context) {
    throw new Error("useRecaptcha must be used within a RecaptchaStateProvider")
  }
  return context
}

export const recaptchaErrorhandler = (error: any) => {
  switch (error?.code) {
    case "auth/too-many-requests":
      showErrorToast("Too many requests. Please try again later.")
      break
    case "auth/invalid-phone-number":
      showErrorToast("The phone number is invalid.")
      break
    case "auth/operation-not-allowed":
      showInfoToast("Our service is not available in your country")
      break
    case "auth/recaptcha-expired/re-auth-denied":
      // no action
      break
    default:
      if (
        error instanceof TypeError &&
        error?.message.includes("verifier.verify")
      ) {
        showErrorToast("Please fill the captcha, or let the captcha load")
      } else {
        showErrorToast("Something went wrong. Please try again later.")
      }
      break
  }
}

const RecaptchaStateProvider = ({ children }: { children: ReactNode }) => {
  const recaptchaVerifiedState = useRef<boolean>(false)
  const verifier = useRef<RecaptchaVerifier | null>(null)
  const isProcessing = useRef<boolean>(false)

  const checkRecaptchaElementExists = useCallback(() => {
    const existingRecaptcha = document.querySelector("#recaptcha-container")
    if (existingRecaptcha) {
      return {
        status: true,
        element: existingRecaptcha,
      }
    }
    return {
      status: false,
      element: null,
    }
  }, [])

  const embedTheRecaptchaElement = useCallback(
    (recaptchaRef: React.MutableRefObject<HTMLDivElement | null>) => {
      console.debug("Embedding the element")
      const innerDiv = document.createElement("div")
      innerDiv.id = "recaptcha-container"
      innerDiv.className = "recaptcha-container"
      recaptchaRef.current?.appendChild(innerDiv)
    },
    [],
  )

  const removeRecaptchaElement = useCallback(() => {
    console.debug("Removing the element")
    const { status, element } = checkRecaptchaElementExists()
    if (status) {
      element?.remove()
    }
  }, [checkRecaptchaElementExists])

  const clearRecaptchaToken = useCallback(() => {
    try {
      if (verifier.current) {
        verifier.current?.clear()
        verifier.current = null
      }
    } catch (error) {
      console.warn("Error clearing recaptcha verifier:", error)
      verifier.current = null
    }

    recaptchaVerifiedState.current = false
    isProcessing.current = false

    removeRecaptchaElement()
  }, [removeRecaptchaElement])

  const initializeTheRecaptchaVerifier = useCallback(
    (recaptchaRef: React.MutableRefObject<HTMLDivElement | null>) => {
      if (!recaptchaRef?.current) return

      try {
        console.debug("Initializing the recaptcha verifier")
        // Always clear any existing verifier first
        console.debug("If verifier exists, clearing the verifier")
        if (verifier.current) {
          console.debug("Clearing the verifier")
          verifier.current?.clear()
          verifier.current = null
        }

        console.debug("Checking if the element exists")
        // Always remove existing element to prevent DUPE errors
        const { status: elementStatus } = checkRecaptchaElementExists()
        if (elementStatus) {
          removeRecaptchaElement()
        }

        // Create fresh element
        embedTheRecaptchaElement(recaptchaRef)

        console.debug("Creating new verifier instance")
        // Create new verifier instance
        verifier.current = new RecaptchaVerifier(auth, "recaptcha-container", {
          size: "invisible",
          callback: (_id) => {
            recaptchaVerifiedState.current = true
            Sentry.captureMessage("The Recaptcha is verified")
          },
          "expired-callback": () => {
            console.debug("Recaptcha expired, clearing token")
            clearRecaptchaToken() // Use full clear for expiration to prevent token reuse
          },
          "error-callback": (error) => {
            console.error("Recaptcha error:", error)
            recaptchaErrorhandler(error)
            clearRecaptchaToken() // Use full clear for errors
          },
        })

        console.debug("Recaptcha verifier initialized successfully")
      } catch (error) {
        console.error("Error initializing recaptcha verifier:", error)
        Sentry.captureException(error)
        clearRecaptchaToken()
      }
    },
    [clearRecaptchaToken, removeRecaptchaElement, embedTheRecaptchaElement],
  )

  return (
    <RecaptchaContext.Provider
      value={{
        verifier,
        recaptchaVerifiedState,
        recaptchaErrorhandler,
        removeRecaptchaElement,
        embedTheRecaptchaElement,
        clearRecaptchaToken,
        isProcessing,
        initializeTheRecaptchaVerifier,
      }}
    >
      {children}
    </RecaptchaContext.Provider>
  )
}

export default RecaptchaStateProvider
